import { createContext, useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { fetchMe, logout, type Me } from '@/api/auth';
import { setLocale } from '@/i18n';
import { tileCache } from '@/map/tileCache';

type Status = 'loading' | 'anonymous' | 'authenticated';
export interface AuthState {
  me: Me | null;
  status: Status;
  refresh: () => Promise<Me | null>;
  setMe: (me: Me) => void;
  signOut: () => Promise<void>;
}
export const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [me, setMeState] = useState<Me | null>(null);
  const [status, setStatus] = useState<Status>('loading');
  const setMe = useCallback((m: Me) => {
    setMeState(m);
    setStatus('authenticated');
    setLocale(m.locale as 'cs' | 'en');
  }, []);
  const refresh = useCallback(async () => {
    const m = await fetchMe();
    if (m) setMe(m);
    else { setMeState(null); setStatus('anonymous'); }
    return m;
  }, [setMe]);
  // The tile cache keeps tiles without their token: nothing of this user's stays for the next one.
  const signOut = useCallback(async () => { await logout(); await tileCache.clear(); setMeState(null); setStatus('anonymous'); }, []);
  // Mount-time sync with the server session (an external system); the lint rule can't see
  // that the setState calls happen inside the awaited async callback, not the effect body itself.
  // eslint-disable-next-line react-hooks/set-state-in-effect
  useEffect(() => { void refresh(); }, [refresh]);
  const value = useMemo(() => ({ me, status, refresh, setMe, signOut }), [me, status, refresh, setMe, signOut]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
